import { confirmar } from "@/componentes/pergunta.tsx";
import { useAcoes, useEstado } from "../estado.jsx";
import { progressoDoFunil } from "../caso.js";
import { ICONES, Icone } from "../visual.jsx";
import { Gravou } from "./comuns.jsx";

/**
 * Onde o caso está no funil — uma barra segmentada, inteira na tela, e operável.
 *
 * A mesa fazia o processo inteiro e não o mostrava. As etapas existiam numa
 * pilha vertical dentro do painel (que só está aberto às vezes), e em lugar
 * nenhum se lia "andou 5 de 10" ou "parado há 9 dias" — que é a informação que
 * faz alguém agir.
 *
 * **Barra segmentada, e não uma fila de pontos com nome** (27/08, terceira
 * passada — as duas primeiras estão aqui porque cada uma explica por que a
 * seguinte existe):
 *
 *   1. Fila de pontos com o rótulo embaixo, cada passo dividindo a largura
 *      (`flex-1`). Com dez etapas numa conversa de 640px o rótulo quebrava com
 *      hífen — "Con-trato assi-nado", "Elabo-ração do pro-cesso": quatro linhas
 *      por passo e 130px de altura no alto de TODA conversa. Hifenização
 *      automática em coluna de 48px não parte palavra, moe.
 *   2. Mesma fila, rótulo em uma linha e rolagem horizontal. Sumiu a quebra e
 *      entraram dois defeitos piores: o `overflow-x-auto` corta na VERTICAL
 *      também, decepando o anel do ponto aceso pelo topo, e metade do caminho
 *      passou a viver fora da tela — trilho que existe para mostrar o caminho
 *      inteiro e esconde seis das dez etapas atrás de um arrasto não mostra
 *      nada.
 *   3. Esta. Todos os segmentos na tela, sempre, sem rolagem e sem nada cortado:
 *      a largura se divide entre eles e a COR faz o trabalho — verde é o que
 *      passou, terracota é onde o caso está, cinza é o que falta, âmbar marca a
 *      decisão que trava o avanço. É o mesmo idioma que o filtro da mesa já usa
 *      para o funil (`FiltroDaMesa.jsx`), e o escritório já o lê ali.
 *
 * O que se ganha e o que se perde, escrito: o segmento ATUAL leva o nome dentro
 * dele (é a etapa que a pessoa precisa ler) e a contagem fica ao lado; os outros
 * nove não têm nome escrito, e o nome deles chega por três caminhos —
 * `data-dica` no ponteiro, `aria-label` no leitor de tela, e a pergunta de
 * `confirmar()` antes de mover, que diz para onde o caso vai. A lista com os dez
 * nomes escritos continua existindo no bloco ETAPA da ficha, que é onde se
 * escolhe de propósito.
 *
 * **Daqui se MOVE.** Era leitura, e o argumento contra era bom: alvo pequeno no
 * caminho do ponteiro o dia inteiro é escrita no funil por engano. O que resolve
 * não é proibir o clique — é o TAMANHO (o segmento inteiro, 28px de altura) e a
 * PERGUNTA. A etapa atual não é botão: é onde o caso já está.
 *
 * A conta mora em `caso.js` (`progressoDoFunil`), com teste: qual etapa está
 * feita, o que fazer com a etapa de perda e de onde sai o "desde" são decisões
 * que envelhecem — o funil é configurável em tempo de execução.
 */
export default function TrilhoDoProcesso() {
  const { caso, etapas } = useEstado();
  const { moverEtapa } = useAcoes();
  const stage = caso?.registro?.stage;
  if (!stage || !etapas?.length) return null;

  const p = progressoDoFunil(etapas, stage, caso?.trilha);
  if (p.indice < 0) return null;

  /* O portão: existe um passo pendente que pede confirmação — é o que trava o
     avanço e é decisão de gente. `pauta.passos` já traz isso pronto do
     servidor, com a permissão que cada um exige. */
  const temPortao = (caso?.pauta?.passos ?? []).some((passo) => passo?.confirmar);

  const mover = async (etapa) => {
    if (await confirmar({ titulo: `Mover para "${etapa.rotulo}"?`, acao: "Mover" })) moverEtapa(etapa.chave);
  };

  /*
   * DESQUALIFICAR — o lead que nunca foi caso, e por que é botão e não item de lista.
   *
   * Arquivar e desqualificar pareciam a mesma coisa e não são: arquivar guarda
   * quem parou de responder (o escritório ainda faz campanha de retomada com
   * esses), desqualificar diz que não há o que retomar — é propaganda, robô,
   * alguém que clicou sem querer, ou quem não tem o requisito. Sem a diferença
   * escrita, "quantos chegaram e quantos não eram caso" — a conta que decide
   * verba de anúncio — não existe.
   *
   * Fica AQUI, ao lado do caminho, porque é o fim do caminho: o segmento dela
   * não aparece no trilho (etapa de perda só é desenhada quando é a atual), e
   * enterrá-la na lista de etapas da ficha faria a saída custar mais que o
   * atendimento. Vermelho e pequeno: destaque de consequência, não de frequência.
   */
  const desqualificar = etapas.find((e) => e.chave === "desqualificado");
  const jaMorreu = Boolean(p.desistiu);

  const marcarDesqualificado = async () => {
    const ok = await confirmar({
      titulo: "Desqualificar este contato?",
      texto:
        'Ele sai do funil e passa a contar como "não era caso" nas métricas. Não é arquivar: arquivado volta numa campanha de retomada, desqualificado não.',
      acao: "Desqualificar",
      perigo: true,
    });
    if (ok) moverEtapa(desqualificar.chave);
  };

  return (
    /* O recibo verde é o mesmo do bloco ETAPA da ficha, e pela mesma razão:
       mover dispara régua, fila e prazo, e quem tocou precisa ver onde
       aconteceu. Mesma chave dos dois lados — mover pelo trilho e mover pela
       ficha são a mesma escrita, e piscar nos dois lugares seria dizer que
       foram duas. */
    <Gravou chave={`etapa:${caso.registro.id}`}>
      {/* No CELULAR a barra fica com a LINHA INTEIRA e a leitura desce para a
          segunda: a contagem, o "espera você" e o botão de desqualificar
          comiam a largura, e o caminho do processo virava dez tracinhos de 4px
          — trilho que existe para mostrar o caminho e não mostra nada. O header
          tem altura de sobra no telefone; a largura é que não tem. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-4 pb-2.5">
        <div
          /* Deixou de ser `aria-hidden`: era leitura redundante do cabeçalho e do
             `RadioGroup` da ficha, e esconder era o certo. Agora aqui se OPERA, e
             controle dentro de subárvore escondida é foco que existe para o
             teclado e não existe para o leitor de tela — o pior dos dois mundos. */
          role="group"
          aria-label={`Etapas do processo — ${p.passos[p.indice].rotulo}, ${p.indice + 1} de ${p.total}. Toque para mover.`}
          /* Sem `overflow` NENHUM: era ele que decepava o anel do ponto aceso
             pelo topo, e é ele que esconderia metade do caminho. O que não cabe
             não sai da tela — encolhe, e a cor continua dizendo tudo. */
          className="flex w-full min-w-0 flex-1 basis-full items-stretch gap-[3px] select-none sm:w-auto sm:basis-auto"
        >
          {p.passos.map((etapa, i) => {
            const feita = etapa.estado === "feita";
            const agora = etapa.estado === "agora";
            /* O âmbar é do PRÓXIMO passo, não do atual: o que espera decisão é a
               passagem, e é lá que o olho precisa parar. */
            const portao = temPortao && i === p.indice + 1;
            const morreu = agora && p.desistiu;
            /* A etapa atual não é botão: ela é onde o caso já está, e um alvo que
               não faz nada é o alvo que ensina que nenhum deles faz. */
            const Passo = agora ? "div" : "button";

            return (
              <Passo
                key={etapa.chave}
                {...(agora
                  ? {}
                  : {
                      type: "button",
                      onClick: () => mover(etapa),
                      "aria-label": `Mover para ${etapa.rotulo}`,
                      /* O nome da etapa chega pelo ponteiro: o segmento não tem
                         largura para escrevê-lo, e a dica é o que o substitui —
                         com o que vai acontecer dito junto. */
                      "data-dica": `${etapa.rotulo} — mover o caso para cá (pergunta antes)`,
                    })}
                /*
                 * O segmento ATUAL leva cinco partes da largura e os outros uma:
                 * é ele que precisa caber um nome. Com dez etapas numa conversa
                 * de 700px isso dá ~250px para o nome e ~32px para cada vizinho —
                 * e `min-w-[10px]` garante que nenhum deles desapareça, porque
                 * segmento invisível é etapa que a barra deixou de contar.
                 */
                className={`relative flex h-mini items-center justify-center overflow-hidden border-none p-0 transition-[flex,background-color] duration-[var(--t-media)] ease-entra ${
                  agora ? "flex-[5] min-w-0" : "min-w-[10px] flex-1 cursor-pointer"
                } ${i === 0 ? "rounded-l-full" : ""} ${i === p.passos.length - 1 ? "rounded-r-full" : "rounded-r-[4px]"} ${
                  i === 0 ? "" : "rounded-l-[4px]"
                } ${
                  morreu
                    ? "bg-tinta-fantasma"
                    : agora
                      ? "bg-terra"
                      : feita
                        ? "bg-verde hover:bg-verde-vivo"
                        : /* O portão NÃO escurece no hover: ia para o âmbar
                             escuro e o "!" em tinta sumia justo quando o
                             ponteiro chegava para ler. O retorno é um véu. */
                          portao
                          ? "bg-ambar hover:bg-ambar/80"
                          : "bg-foreground/12 hover:bg-foreground/20"
                }`}
              >
                {/* O nome, só no segmento onde o caso está — é o único que tem
                    largura para ele, e o único que a pessoa precisa LER. */}
                {agora ? (
                  <span className="truncate px-2.5 text-selo font-bold tracking-[.02em] whitespace-nowrap text-sobre-terra">
                    {etapa.rotulo}
                  </span>
                ) : feita ? (
                  /* O ✓ some sozinho quando o segmento fica estreito: `overflow-hidden`
                     no segmento corta o ícone em vez de esticá-lo, e aí a cor
                     continua dizendo "passou" sem nada torto no meio. */
                  <Icone de={ICONES.certo} tamanho={14} cor="var(--color-sobre-terra)" />
                ) : portao ? (
                  <span className="text-selo leading-none font-bold text-sobre-ambar">!</span>
                ) : null}
              </Passo>
            );
          })}
        </div>

        {/* A contagem e o tempo FORA da barra: são leitura sobre ela, e dentro de
            um segmento de 32px não caberiam. "há 9d" é o que diferencia um caso
            que anda de um caso esquecido, e o portão diz que a bola está com a
            gente. */}
        <span className="flex flex-none items-center gap-2 font-mono text-selo whitespace-nowrap text-muted-foreground">
          <span className="font-bold text-tinta-suave">
            {p.indice + 1}/{p.total}
          </span>
          {p.ha ? <span>{p.ha}</span> : null}
          {temPortao ? <span className="font-bold text-ambar-escuro">espera você</span> : null}
        </span>

        {desqualificar && !jaMorreu ? (
          <button
            type="button"
            onClick={marcarDesqualificado}
            aria-label="Desqualificar este contato"
            data-dica="Não era caso — propaganda, robô, sem requisito. Sai do funil e conta na métrica."
            /* O "perigo mini" da casa (28, raio 10): era uma pílula de 22 com
               letra de selo, a menor coisa da faixa para a decisão mais cara. */
            className="alvo flex h-mini flex-none cursor-pointer items-center gap-1.5 rounded-controle border-0 bg-destructive/10 px-2.5 text-apoio font-semibold text-destructive transition-colors hover:bg-destructive/18"
          >
            <Icone de={ICONES.fechar} tamanho={14} cor="currentColor" />
            Desqualificar
          </button>
        ) : null}
      </div>
    </Gravou>
  );
}
